import {format} from 'date-fns';
import last from 'lodash/last';
import {requestContext} from '../../../../common/requestContext';
import {twMerge} from '../../../../common/twMerge';
import useTypedSelector from '../../../../common/useTypedSelector';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {Maybe, SortOption} from '../../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {ReactQueryInfiniteType} from '../../../../reactQuery/CodegenReactQueryType.type';
import {ReactQueryFetcher} from '../../../../reactQuery/ReactQueryFetcher';
import {ReactQueryInfiniteFetcher} from '../../../../reactQuery/ReactQueryInfiniteFetcher';
import {
  useBlockUserListQuery,
  useInfiniteCommentListQuery,
} from '../../../../wrapper';
import {
  EMPTY_COMMENT_CONTENT_HOME_TEXT,
  EMPTY_COMMENT_TEXT,
} from '../../../comment/constant';
import {CommentItemContainer} from '../../../comment/detail/container/CommentItemContainer';

export interface CommentAllListContainerProps {
  seriesId: number;
  productId?: number;
  isContentHome?: boolean;
  initialSortType?: Maybe<string>;
  selectedSortOpt?: Maybe<SortOption>;
}

export function CommentAllListContainer({
  seriesId,
  productId,
  isContentHome,
  initialSortType,
  selectedSortOpt,
}: CommentAllListContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const user = useTypedSelector(state => state.user.user);
  return (
    <div className="flex flex-1 flex-col">
      <ReactQueryFetcher
        reactQuery={useBlockUserListQuery}
        options={{enabled: !!user}}
        loadingWrapperClassName={twMerge(
          !isMobile && isContentHome && 'bg-theme-bg-surface-alt'
        )}
      >
        <ReactQueryInfiniteFetcher
          reactQuery={useInfiniteCommentListQuery}
          variables={{
            commentListInput: {
              page: 0,
              seriesId,
              productId,
              sortType: selectedSortOpt?.param || initialSortType,
            },
          }}
          options={{
            initialPageParam: {
              commentListInput: {
                page: 0,
                seriesId,
                productId,
                // 선택된 값이 없고 query string으로 초기값을 주어졌을 때 초기값을 사용합니다.
                sortType: selectedSortOpt?.param || initialSortType,
              },
            },
            getNextPageParam: (lastPage, pages) => {
              const lastComment = last(lastPage.commentList?.commentList || []);
              return lastPage.commentList?.isEnd
                ? undefined
                : {
                    commentListInput: {
                      seriesId,
                      productId,
                      page: pages.length,
                      sortType: selectedSortOpt?.param || initialSortType,
                      lastCommentUid: lastComment?.commentUid,
                      lastLikeCount: lastComment?.likeCount,
                      lastCreatedDt: lastComment?.createDt
                        ? format(
                            new Date(lastComment.createDt),
                            'yyyy-MM-dd HH:mm:ss'
                          )
                        : undefined,
                    },
                  };
            },
          }}
          type={
            !isMobile && isContentHome
              ? ReactQueryInfiniteType.ArrowDown
              : ReactQueryInfiniteType.InfinityScroll
          }
          onStartFetchNextPage={e =>
            LOG_CLICK_EVENT('댓글목록_가져오기_클릭', e)
          }
        >
          {commentList => {
            if (
              commentList &&
              commentList.pages[0].commentList?.commentList.length === 0
            )
              return (
                <NoItem isMobile={isMobile}>
                  {isContentHome
                    ? EMPTY_COMMENT_CONTENT_HOME_TEXT
                    : EMPTY_COMMENT_TEXT}
                </NoItem>
              );

            return commentList?.pages.map(page =>
              page.commentList?.commentList.map(item => (
                <CommentItemContainer
                  seriesId={seriesId}
                  productId={productId}
                  sortType={selectedSortOpt?.param || initialSortType}
                  item={item}
                  key={`${item.commentUid}_${selectedSortOpt?.param}`}
                  isProductComment={!!productId}
                  isContentHome={isContentHome}
                />
              ))
            );
          }}
        </ReactQueryInfiniteFetcher>
      </ReactQueryFetcher>
    </div>
  );
}
